import React, { useCallback, useEffect, useRef, useState } from "react";
import "./styles/App.css";
import { GameCanvas } from "./components/GameCanvas";
import { LoginScreen } from "./components/LoginScreen";
import { WorldMap } from "./components/WorldMap";
import { FactionWarfareMapWrapper } from "./components/FactionWarfareMapWrapper";
import { TransitionOverlay } from "./components/TransitionOverlay";
import { PrivacyPolicy } from "./components/PrivacyPolicy";
import { TermsAndConditions } from "./components/TermsAndConditions";
import { Tutorial } from "./components/Tutorial";
import { ProductionAdminPanel } from "./components/ProductionAdminPanel";
import { MonitoringDashboard } from "./pages/MonitoringDashboard";
import { HiscoresPage } from "./pages/HiscoresPage";
import { HistorySimulator } from "./components/HistorySimulator";
import { Testbed } from "./components/Testbed";
import { AuthProvider } from "./contexts/AuthContext";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import { MobileSupportOverlay } from "./components/MobileSupportOverlay";

interface PlayerInfo {
  name: string;
  character: string;
  shipColor: string;
  characterId?: string; // For authenticated users with saved characters
  accountId?: string; // For authenticated users - needed for persistence
  token?: string; // JWT token for server-side auth verification
  savedPosition?: { x: number; y: number }; // Saved character position
  // Saved character stats
  savedStats?: {
    berries: number;
    level: number;
    xp: number;
    wildcardPowers?: unknown[];
    equippedWildcardPower?: string | null;
    infamy: number;
  };
  // Saved skills snapshot from Supabase characters table
  savedSkills?: unknown;
  startingOcean?: string; // Ocean region to spawn in for new characters
}

export interface Destination {
  x: number;
  y: number;
  name?: string; // Optional name (e.g., island name)
}

type View =
  | "login"
  | "game"
  | "worldmap"
  | "factionwarfare"
  | "privacy"
  | "terms"
  | "admin"
  | "monitor"
  | "hiscores"
  | "history"
  | "testbed";

interface PirateRaidData {
  id: string;
  islandId: string;
  islandName: string;
  islandX: number;
  islandY: number;
  tier: string;
  expiresAt: number;
  piratesDefeated: number;
  totalPirates: number;
  rewardPool: number;
}

interface SeaMonsterData {
  id: string;
  type: string;
  x: number;
  y: number;
}

interface RiftScarData {
  id: string;
  x: number;
  y: number;
  name: string;
}

// Key for localStorage to track if tutorial has been seen
const TUTORIAL_SEEN_KEY = "grand_line_tutorial_seen";

const TRANSITION_MIN_DURATION_MS = 250;

const App: React.FC = () => {
  const [playerInfo, setPlayerInfo] = useState<PlayerInfo | null>(null);
  const [showTutorial, setShowTutorial] = useState<boolean>(false);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [transitionMessage, setTransitionMessage] =
    useState<string>("Loading...");
  const transitionStartedAtMsRef = useRef<number>(0);
  const pendingTargetViewRef = useRef<View | null>(null);
  const hasBootedGameRef = useRef<boolean>(false);

  // Determine initial view from URL path
  const getInitialView = (): View => {
    const path = window.location.pathname;
    if (path === "/privacy") return "privacy";
    if (path === "/terms") return "terms";
    if (path === "/admin") return "admin";
    if (path === "/monitor") return "monitor";
    if (path === "/hiscores") return "hiscores";
    if (path === "/history") return "history";
    if (path === "/testbed") return "testbed";
    return "login";
  };

  const [currentView, setCurrentView] = useState<View>(getInitialView);
  const [playerPosition, setPlayerPosition] = useState<{
    x: number;
    y: number;
  }>({ x: 500, y: 500 });
  const [destination, setDestination] = useState<Destination | null>(null);
  const [riftStorms, setRiftStorms] = useState<
    Array<{
      id: string;
      x: number;
      y: number;
      radius: number;
      tier: string;
      color: string;
      expiresAt: number;
    }>
  >([]);
  const [pirateRaids, setPirateRaids] = useState<PirateRaidData[]>([]);
  const [seaMonsters, setSeaMonsters] = useState<SeaMonsterData[]>([]);
  const [riftScars, setRiftScars] = useState<RiftScarData[]>([]);

  const startTransition = useCallback(
    (message: string, targetView: View): void => {
      transitionStartedAtMsRef.current = Date.now();
      pendingTargetViewRef.current = targetView;
      setTransitionMessage(message);
      setIsTransitioning(true);
    },
    [],
  );

  const endTransitionIfCurrent = useCallback((view: View): void => {
    if (pendingTargetViewRef.current !== view) return;
    const elapsedMs = Date.now() - transitionStartedAtMsRef.current;
    const remainingMs = Math.max(0, TRANSITION_MIN_DURATION_MS - elapsedMs);
    window.setTimeout(() => {
      if (pendingTargetViewRef.current === view) {
        setIsTransitioning(false);
        pendingTargetViewRef.current = null;
      }
    }, remainingMs);
  }, []);

  const handleGameReady = useCallback((): void => {
    hasBootedGameRef.current = true;
    endTransitionIfCurrent("game");
  }, [endTransitionIfCurrent]);

  const handleWorldMapReady = useCallback((): void => {
    endTransitionIfCurrent("worldmap");
  }, [endTransitionIfCurrent]);

  const handleJoinGame = (
    name: string,
    character: string,
    shipColor: string,
    characterId?: string,
    accountId?: string,
    savedPosition?: { x: number; y: number },
    savedStats?: {
      berries: number;
      level: number;
      xp: number;
      infamy: number;
      wildcardPowers?: unknown[];
      equippedWildcardPower?: string | null;
    },
    savedSkills?: unknown,
    startingOcean?: string,
    token?: string,
  ): void => {
    startTransition("Loading world...", "game");
    setPlayerInfo({
      name,
      character,
      shipColor,
      characterId,
      accountId,
      token,
      savedPosition,
      savedStats,
      savedSkills,
      startingOcean,
    });

    // Check if this is a new character (no saved position means fresh start)
    // Also check localStorage to see if tutorial has been seen before
    const tutorialSeen = localStorage.getItem(TUTORIAL_SEEN_KEY);
    const isNewCharacter = !savedPosition;

    if (isNewCharacter && !tutorialSeen) {
      setShowTutorial(true);
    }

    setCurrentView("game");
  };

  const handleTutorialComplete = (): void => {
    localStorage.setItem(TUTORIAL_SEEN_KEY, "true");
    setShowTutorial(false);
  };

  const handleTutorialSkip = (): void => {
    localStorage.setItem(TUTORIAL_SEEN_KEY, "true");
    setShowTutorial(false);
  };

  const handleShowTutorial = (): void => {
    setShowTutorial(true);
  };

  const handleOpenWorldMap = (position: { x: number; y: number }): void => {
    startTransition("Opening world map...", "worldmap");
    setPlayerPosition(position);
    // Update playerInfo.savedPosition so when returning from world map,
    // the player spawns at their current position (important for guests)
    // IMPORTANT: Preserve savedStats to prevent berries from resetting
    if (playerInfo) {
      setPlayerInfo({
        ...playerInfo,
        savedPosition: position,
        // Explicitly preserve savedStats to prevent state loss
        savedStats: playerInfo.savedStats,
      });
    }
    setCurrentView("worldmap");
  };

  const handleCloseWorldMap = (): void => {
    startTransition("Returning to sea...", "game");
    setCurrentView("game");
    // GameCanvas stays mounted; if we've already booted once, don't wait for onReady again.
    if (hasBootedGameRef.current) {
      endTransitionIfCurrent("game");
    }
  };

  const handleOpenFactionWarfare = (): void => {
    setCurrentView("factionwarfare");
  };

  const handleCloseFactionWarfare = (): void => {
    setCurrentView("game");
    if (hasBootedGameRef.current) {
      endTransitionIfCurrent("game");
    }
  };

  useEffect(() => {
    if (currentView === "login") {
      setIsTransitioning(false);
      pendingTargetViewRef.current = null;
    }
  }, [currentView]);

  const handleNavigateToPrivacy = (): void => {
    window.history.pushState({}, "", "/privacy");
    setCurrentView("privacy");
  };

  const handleNavigateToTerms = (): void => {
    window.history.pushState({}, "", "/terms");
    setCurrentView("terms");
  };

  const handleBackToLogin = (): void => {
    window.history.pushState({}, "", "/");
    setCurrentView("login");
  };

  return (
    <AuthProvider>
      <ConfirmProvider>
        <div className="app">
          <MobileSupportOverlay />
          <TransitionOverlay
            isVisible={isTransitioning}
            message={transitionMessage}
          />
          {currentView === "login" && (
            <LoginScreen
              onJoin={handleJoinGame}
              onNavigateToPrivacy={handleNavigateToPrivacy}
              onNavigateToTerms={handleNavigateToTerms}
            />
          )}
          {playerInfo &&
            (currentView === "game" ||
              currentView === "worldmap" ||
              currentView === "factionwarfare") && (
              <div
                style={{
                  display: currentView === "game" ? "flex" : "none",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <GameCanvas
                  playerInfo={playerInfo}
                  onOpenWorldMap={handleOpenWorldMap}
                  onOpenFactionWarfare={handleOpenFactionWarfare}
                  destination={destination}
                  onClearDestination={() => setDestination(null)}
                  onSetDestination={setDestination}
                  onReady={handleGameReady}
                  onRiftStormsChange={setRiftStorms}
                  onPirateRaidsChange={setPirateRaids}
                  onSeaMonstersChange={setSeaMonsters}
                  onRiftScarsChange={setRiftScars}
                  onShowTutorial={handleShowTutorial}
                />
              </div>
            )}
          {currentView === "worldmap" && playerInfo && (
            <WorldMap
              playerInfo={playerInfo}
              playerPosition={playerPosition}
              onClose={handleCloseWorldMap}
              destination={destination}
              onSetDestination={setDestination}
              onReady={handleWorldMapReady}
              riftStorms={riftStorms}
              pirateRaids={pirateRaids}
              seaMonsters={seaMonsters}
              riftScars={riftScars}
            />
          )}
          {currentView === "factionwarfare" && (
            <FactionWarfareMapWrapper onClose={handleCloseFactionWarfare} />
          )}
          {currentView === "privacy" && (
            <PrivacyPolicy onBack={handleBackToLogin} />
          )}
          {currentView === "terms" && (
            <TermsAndConditions onBack={handleBackToLogin} />
          )}
          {currentView === "admin" && (
            <ProductionAdminPanel
              apiBaseUrl={
                process.env.REACT_APP_API_URL || "http://localhost:3001"
              }
            />
          )}
          {currentView === "monitor" && (
            <MonitoringDashboard
              apiBaseUrl={
                process.env.REACT_APP_API_URL || "http://localhost:3001"
              }
            />
          )}
          {currentView === "hiscores" && <HiscoresPage />}
          {currentView === "history" && <HistorySimulator />}
          {currentView === "testbed" && <Testbed onBack={handleBackToLogin} />}
          {showTutorial && (
            <Tutorial
              onComplete={handleTutorialComplete}
              onSkip={handleTutorialSkip}
            />
          )}
        </div>
      </ConfirmProvider>
    </AuthProvider>
  );
};

export default App;
